/**
 * «Crear las plantillas en Meta» en Mensajes (la parte C del 01/11/2026), lógica pura de la pantalla.
 *
 * - **Qué plantilla se creó desde cada propuesta** (`Creadas`): la vista la trae (`plantillaIa`) y la respuesta de crear
 *   la actualiza. Si el operador edita la propuesta después, ese grupo vuelve a quedar «para crear»: se crea otra y el
 *   grupo pasa a la nueva (la anterior queda en Meta).
 * - **El estado de cada grupo** en palabras: «En revisión de Meta», «Aprobada» o «Rechazada: motivo». Sale de la lista
 *   de plantillas de la marca, que mantienen al día el polling y «Consultar a Meta» del oficial.
 * - **Cuántas plantillas** se van a crear: una por texto (Meta rechaza dos iguales, y el backend las comparte).
 * - Nada de esto crea ni manda nada: lo hace el backend, sólo cuando el operador confirma.
 */

import type { EstadoPlantilla, PlantillaResumen } from "../../wa-oficial/types";
import type { Borrador } from "./mensajes";
import type { PropuestaIa, RespuestaDePlantillas, VistaDeMensajes } from "../types";

/** La propuesta como texto, con las claves siempre en el mismo orden: para saber si cambió desde que se creó. */
export function firmaDePropuesta(p: PropuestaIa | null | undefined): string {
  if (!p) return "";
  return JSON.stringify({
    cuerpo: p.cuerpo,
    pie: p.pie,
    botones: p.botones.map((b) => ({ tipo: b.tipo, titulo: b.titulo, id: b.id })),
    variables: [...p.variables].sort((a, b) => a.numero - b.numero).map((v) => ({ numero: v.numero, alias: v.alias, ejemplo: v.ejemplo, campoSugerido: v.campoSugerido ?? null })),
  });
}

/** La plantilla que se creó con la propuesta de cada grupo, y la firma de esa propuesta ("" = de otra, ya cambiada). */
export type Creadas = Record<string, { plantillaId: string; propuesta: string }>;

export function creadasDeLaVista(v: Pick<VistaDeMensajes, "grupos">): Creadas {
  const out: Creadas = {};
  for (const g of v.grupos) {
    if (!g.plantillaIa?.plantillaId) continue;
    out[g.id] = { plantillaId: g.plantillaIa.plantillaId, propuesta: g.plantillaIa.alDia ? firmaDePropuesta(g.guardado?.propuestaIa) : "" };
  }
  return out;
}

/** Lo que se sabe de una plantilla: de la lista de la marca o, hasta que llegue, de la respuesta de crear. */
export interface EstadoConocido {
  estado: EstadoPlantilla | string;
  motivo: string;
  nombre: string;
}

/**
 * El estado de una plantilla: el de la lista (la más fresca); si no está, el de la respuesta de crear (la lista todavía
 * no la trae). Con la lista cargada y sin ninguno de los dos, ya no está (archivada o borrada): `no_esta`. Sin la lista
 * ni la respuesta, no se sabe (undefined).
 */
export function estadosConocidos(plantillas: PlantillaResumen[] | null, respuestas: Record<string, EstadoConocido>): (id: string) => EstadoConocido | undefined {
  const deLaLista = new Map((plantillas ?? []).map((p) => [p.id, { estado: p.estado, motivo: p.rejectionReason ?? "", nombre: p.nombreVisible }]));
  return (id) => deLaLista.get(id) ?? respuestas[id] ?? (plantillas === null ? undefined : { estado: "no_esta", motivo: "", nombre: "" });
}

export type EstadoDelGrupo =
  /** sin propuesta: la plantilla se elige a mano o se escribe con IA */
  | { tipo: "sin_propuesta" }
  /** con propuesta y sin crear (o la propuesta cambió, o la anterior ya no sirve) */
  | { tipo: "para_crear"; porque: "nueva" | "editada" | "no_sirve" }
  /** se creó y no llegó a Meta: se vuelve a mandar la misma */
  | { tipo: "sin_llegar"; nombre: string }
  | { tipo: "en_revision"; nombre: string }
  | { tipo: "aprobada"; nombre: string }
  | { tipo: "rechazada"; nombre: string; motivo: string }
  /** se creó y todavía no llegó la lista de plantillas */
  | { tipo: "consultando" };

/** El estado de la plantilla que salió de la propuesta del grupo (la misma regla que el backend para crearla o no). */
export function estadoDelGrupo(b: Pick<Borrador, "propuestaIa">, creada: Creadas[string] | undefined, conocido: (id: string) => EstadoConocido | undefined): EstadoDelGrupo {
  if (!b.propuestaIa) return { tipo: "sin_propuesta" };
  if (!creada) return { tipo: "para_crear", porque: "nueva" };
  if (creada.propuesta !== firmaDePropuesta(b.propuestaIa)) return { tipo: "para_crear", porque: "editada" };
  const p = conocido(creada.plantillaId);
  if (!p) return { tipo: "consultando" };
  if (p.estado === "received" || p.estado === "pending") return { tipo: "en_revision", nombre: p.nombre };
  if (p.estado === "approved") return { tipo: "aprobada", nombre: p.nombre };
  if (p.estado === "rejected") return { tipo: "rechazada", nombre: p.nombre, motivo: p.motivo };
  if (p.estado === "borrador" || p.estado === "unsubmitted") return { tipo: "sin_llegar", nombre: p.nombre };
  return { tipo: "para_crear", porque: "no_sirve" };
}

/** Lo que dice la fila del grupo. */
export function textoDelEstado(e: EstadoDelGrupo, b: Pick<Borrador, "plantillaId">): string {
  switch (e.tipo) {
    case "sin_propuesta":
      return b.plantillaId ? "Usa una plantilla elegida a mano." : "Sin propuesta: escribila con IA o elegí una plantilla.";
    case "para_crear":
      return e.porque === "editada"
        ? "Editaste la propuesta: se crea una plantilla nueva (la anterior queda)."
        : e.porque === "no_sirve"
          ? "La plantilla anterior ya no sirve: se crea otra."
          : "Para crear.";
    case "sin_llegar":
      return "No llegó a Meta: se vuelve a mandar.";
    case "en_revision":
      return "En revisión de Meta.";
    case "aprobada":
      return "Aprobada.";
    case "rechazada":
      return `Rechazada: ${e.motivo.trim() || "Meta no dijo el motivo"}.`;
    case "consultando":
      return "Creada: consultando su estado…";
  }
}

/** Los grupos que van por el WhatsApp oficial (los únicos que llevan plantilla de Meta). */
export function gruposPorOficial(v: Pick<VistaDeMensajes, "grupos" | "whatsapp">, borradores: Record<string, Pick<Borrador, "va" | "canal">>): string[] {
  if (!v.whatsapp.oficial) return [];
  return v.grupos.filter((g) => borradores[g.id]?.va && borradores[g.id]?.canal === "whatsapp").map((g) => g.id);
}

/** Los que «Crear las plantillas en Meta» va a crear o a volver a mandar. */
export const porCrear = (e: EstadoDelGrupo) => e.tipo === "para_crear" || e.tipo === "sin_llegar";

/** Lo que Meta compara para el duplicado: el cuerpo con el pie, sin mayúsculas ni espacios de más. */
const textoDe = (p: PropuestaIa) => {
  const cuerpo = p.cuerpo.trimEnd();
  const todo = p.pie.trim() ? `${cuerpo}\n\n${p.pie.trim()}` : cuerpo;
  return todo.normalize("NFC").toLowerCase().replace(/\s+/g, " ").trim();
};

/** Cuántas plantillas salen de esos grupos: una por texto (los grupos con el mismo texto comparten una). */
export function cuantasPlantillas(grupos: string[], borradores: Record<string, Pick<Borrador, "propuestaIa">>): number {
  return new Set(grupos.flatMap((g) => (borradores[g]?.propuestaIa ? [textoDe(borradores[g].propuestaIa as PropuestaIa)] : []))).size;
}

/** «1 plantilla», «3 plantillas». */
export const plantillasEnPalabras = (n: number) => `${n} ${n === 1 ? "plantilla" : "plantillas"}`;

/**
 * Cómo queda la pantalla con la respuesta: cada grupo al que se le puso una plantilla, con ella y su mapeo propuesto (ya
 * guardados en el backend); y qué plantilla salió de la propuesta de cada grupo, con su estado hasta que llegue la lista.
 */
export function aplicarRespuesta(
  borradores: Record<string, Borrador>,
  creadas: Creadas,
  r: Pick<RespuestaDePlantillas, "grupos">,
): { borradores: Record<string, Borrador>; creadas: Creadas; estados: Record<string, EstadoConocido> } {
  const nuevos = { ...borradores };
  const nuevas = { ...creadas };
  const estados: Record<string, EstadoConocido> = {};
  for (const g of r.grupos) {
    const b = nuevos[g.grupoId];
    if (!b) continue;
    if (g.asignada) nuevos[g.grupoId] = { ...b, plantillaId: g.asignada.plantillaId, mapeo: g.asignada.mapeo };
    const salioDeLaPropuesta = g.resultado === "creada" || g.resultado === "reusada" || g.resultado === "ya_estaba" || g.resultado === "rechazada" || (g.resultado === "error" && !!g.plantilla);
    if (g.plantilla && salioDeLaPropuesta) {
      nuevas[g.grupoId] = { plantillaId: g.plantilla.id, propuesta: firmaDePropuesta(b.propuestaIa) };
      estados[g.plantilla.id] = { estado: g.plantilla.estado, motivo: g.plantilla.motivo, nombre: g.plantilla.nombre };
    }
  }
  return { borradores: nuevos, creadas: nuevas, estados };
}

/** Los resultados que el operador tiene que mirar (los que no quedaron creados o en orden), primero los que fallaron. */
export function resultadosParaMirar(r: Pick<RespuestaDePlantillas, "grupos">) {
  const orden = ["invalida", "error", "rechazada", "sin_propuesta"];
  return r.grupos.filter((g) => orden.includes(g.resultado)).sort((a, b) => orden.indexOf(a.resultado) - orden.indexOf(b.resultado));
}
